// useAutomations.js - React hooks for automation management
import { useMemo, useCallback, useState } from 'react';
import { Automation } from '../models/Automation.js';
import { useFirestoreCollection } from './useFirestoreCollection.js';
import { getCurrentUserId } from '../utils/authUtils.js';
import { useUserSession } from '../context/UserSessionContext';

export function useAutomations() {

  const { effectiveUserId } = useUserSession();

  // Build user-scoped collection path
  const collectionPath = effectiveUserId ? `automationsUsers/${effectiveUserId}/automations` : 'automations';

  const {
    data: rawAutomations,
    loading,
    error,
    create: createFirestore,
    update: updateFirestore,
    remove: removeFirestore,
    getById: getByIdFirestore,
    query,
    clearError
  } = useFirestoreCollection(collectionPath, {
    autoLoad: true,
    realTime: true,
    orderBy: 'created_at',
    orderDirection: 'desc'
  });

  // Get automation executions for the user
  const executionsCollectionPath = effectiveUserId ? `automation_executions/${effectiveUserId}/executions` : null;

  const executionsHook = useFirestoreCollection(executionsCollectionPath, {
    autoLoad: !!executionsCollectionPath,
    realTime: true,
    orderBy: 'created_at',
    orderDirection: 'desc'
  });

  // Convert executions to appointmentId => executions[] map
  const executionsByAppointment = useMemo(() => {
    if (!executionsHook.data || executionsHook.data.length === 0) {
      return new Map();
    }

    const executionsMap = new Map();

    executionsHook.data.forEach(execution => {
      // Convert Firestore timestamps to ISO strings
      const processedExecution = {
        ...execution,
        scheduledAt: execution.scheduledAt?.toDate?.()?.toISOString() || execution.scheduledAt,
        executedAt: execution.executedAt?.toDate?.()?.toISOString() || execution.executedAt,
        completedAt: execution.completedAt?.toDate?.()?.toISOString() || execution.completedAt,
        created_at: execution.created_at?.toDate?.()?.toISOString() || execution.created_at,
        updated_at: execution.updated_at?.toDate?.()?.toISOString() || execution.updated_at
      };

      const appointmentId = execution.appointmentId;
      if (appointmentId) {
        if (!executionsMap.has(appointmentId)) {
          executionsMap.set(appointmentId, []);
        }
        executionsMap.get(appointmentId).push(processedExecution);
      }
    });

    // Sort executions within each appointment by created_at (most recent first)
    for (const [appointmentId, executions] of executionsMap.entries()) {
      executions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }

    return executionsMap;
  }, [executionsHook.data]);

  // Convert Firestore data to Automation instances
  const automationInstances = useMemo(() => {
    return rawAutomations.map(automation => Automation.fromObject({
      ...automation,
      created_at: automation.created_at?.toDate?.()?.toISOString() || automation.created_at,
      updated_at: automation.updated_at?.toDate?.()?.toISOString() || automation.updated_at
    }));
  }, [rawAutomations]);

  // Create automation
  const createAutomation = useCallback(async (automationData) => {
    try {
      // Validate the automation data
      const validation = Automation.validate(automationData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Check for duplicate names
      const existingAutomation = automationInstances.find(
        automation => automation.name.toLowerCase() === automationData.name.toLowerCase()
      );
      if (existingAutomation) {
        throw new Error('An automation with this name already exists');
      }

      // Create new automation
      const newAutomation = await createFirestore(automationData);
      return { success: true, automation: Automation.fromObject(newAutomation) };
    } catch (err) {
      const errorMessage = err.message;
      return { success: false, error: errorMessage };
    }
  }, [automationInstances, createFirestore]);

  // Update automation
  const updateAutomation = useCallback(async (id, updateData) => {
    try {
      // Find the automation
      const existingAutomation = rawAutomations.find(a => a.id === id);
      if (!existingAutomation) {
        throw new Error('Automation not found');
      }

      // Create updated automation data
      const updatedData = { ...existingAutomation, ...updateData };

      // Validate the updated data
      const validation = Automation.validate(updatedData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Check for duplicate names (excluding current automation)
      if (updateData.name && updateData.name !== existingAutomation.name) {
        const duplicateAutomation = automationInstances.find(automation =>
          automation.name.toLowerCase() === updateData.name.toLowerCase() && automation.id !== id
        );
        if (duplicateAutomation) {
          throw new Error('An automation with this name already exists');
        }
      }

      // Update the automation
      const updatedAutomation = await updateFirestore(id, updateData);
      return { success: true, automation: Automation.fromObject(updatedAutomation) };
    } catch (err) {
      const errorMessage = err.message;
      return { success: false, error: errorMessage };
    }
  }, [rawAutomations, automationInstances, updateFirestore]);

  // Toggle automation active status
  const toggleAutomation = useCallback(async (id) => {
    try {
      const existingAutomation = rawAutomations.find(a => a.id === id);
      if (!existingAutomation) {
        throw new Error('Automation not found');
      }

      const updatedAutomation = await updateFirestore(id, {
        isActive: !existingAutomation.isActive
      });
      return { success: true, automation: Automation.fromObject(updatedAutomation) };
    } catch (err) {
      const errorMessage = err.message;
      return { success: false, error: errorMessage };
    }
  }, [rawAutomations, updateFirestore]);

  // Delete automation
  const deleteAutomation = useCallback(async (id) => {
    try {
      await removeFirestore(id);
      return { success: true };
    } catch (err) {
      const errorMessage = err.message;
      return { success: false, error: errorMessage };
    }
  }, [removeFirestore]);

  return {
    automations: automationInstances,
    loading,
    error,
    createAutomation,
    updateAutomation,
    toggleAutomation,
    deleteAutomation,
    clearError,
    executionsByAppointment,
    executionsLoading: executionsHook.loading,
    executionsError: executionsHook.error,
  };
}

// Hook for automation statistics
export function useAutomationStats() {
  const { automations, loading } = useAutomations();

  const stats = useMemo(() => {
    const total = automations.length;
    const active = automations.filter(a => a.isActive).length;
    const inactive = total - active;

    const byTriggerType = automations.reduce((acc, automation) => {
      acc[automation.triggerType] = (acc[automation.triggerType] || 0) + 1;
      return acc;
    }, {});

    return {
      total,
      active,
      inactive,
      byTriggerType
    };
  }, [automations]);

  return { stats, loading };
}

// Hook for automation search
export function useAutomationSearch(automations, initialQuery = '') {
  const [query, setQuery] = useState(initialQuery);

  const results = useMemo(() => {
    if (!query.trim()) {
      return automations;
    }

    const searchTerm = query.toLowerCase();
    return automations.filter(automation =>
      automation.name.toLowerCase().includes(searchTerm) ||
      automation.description.toLowerCase().includes(searchTerm) ||
      automation.messageTemplate.toLowerCase().includes(searchTerm)
    );
  }, [automations, query]);

  return {
    query,
    setQuery,
    results
  };
}

// Hook for single automation
export function useAutomation(id) {
  const { automations, loading, error } = useAutomations();

  const automation = useMemo(() => {
    return automations.find(automation => automation.id === id) || null;
  }, [automations, id]);

  return { automation, loading, error };
}

// Hook for filtered automations
export function useFilteredAutomations(filters = {}) {
  const { automations, loading, error } = useAutomations();

  const filteredAutomations = useMemo(() => {
    let filtered = [...automations];

    // Filter by active status
    if (filters.isActive !== undefined) {
      filtered = filtered.filter(automation => automation.isActive === filters.isActive);
    }

    // Filter by trigger type
    if (filters.triggerType) {
      filtered = filtered.filter(automation => automation.triggerType === filters.triggerType);
    }

    // Filter by search query
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(automation =>
        automation.name.toLowerCase().includes(query) ||
        automation.description.toLowerCase().includes(query)
      );
    }

    // Sort
    if (filters.sortBy) {
      filtered.sort((a, b) => {
        switch (filters.sortBy) {
          case 'name':
            return a.name.localeCompare(b.name);
          case 'created':
            return new Date(b.created_at) - new Date(a.created_at);
          case 'updated':
            return new Date(b.updated_at) - new Date(a.updated_at);
          default:
            return 0;
        }
      });
    }

    return filtered;
  }, [automations, filters]);

  return {
    automations: filteredAutomations,
    loading,
    error
  };
}

// Hook for bulk operations
export function useBulkAutomations() {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const { updateAutomation, deleteAutomation } = useAutomations();

  const toggleSelection = useCallback((id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const selectAll = useCallback((automations) => {
    setSelectedIds(new Set(automations.map(a => a.id)));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const bulkToggle = useCallback(async (isActive) => {
    if (selectedIds.size === 0) return { success: false, error: 'No automations selected' };

    try {
      setLoading(true);
      const results = [];

      for (const id of selectedIds) {
        const result = await updateAutomation(id, { isActive });
        results.push({ id, ...result });
      }

      clearSelection();
      return { success: true, results };
    } catch (err) {
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [selectedIds, updateAutomation, clearSelection]);

  const bulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return { success: false, error: 'No automations selected' };

    try {
      setLoading(true);
      const results = [];

      for (const id of selectedIds) {
        const result = await deleteAutomation(id);
        results.push({ id, ...result });
      }

      clearSelection();
      return { success: true, results };
    } catch (err) {
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [selectedIds, deleteAutomation, clearSelection]);

  return {
    selectedIds: Array.from(selectedIds),
    selectedCount: selectedIds.size,
    toggleSelection,
    selectAll,
    clearSelection,
    bulkToggle,
    bulkDelete,
    loading
  };
}